import { Link, Route, Routes } from 'react-router-dom';
import { Layout } from './rutas/Layout';
import { RequiereRol, RutaProtegida } from './rutas/RutaProtegida';
import { Administracion } from './paginas/Administracion';
import { Inicio } from './paginas/Inicio';
import { Login } from './paginas/Login';
import { DetalleDeComprobante, PantallaDeComprobantes, PantallaDeEmision } from './comprobantes';
import { PantallaDelContrato } from './contrato';
import { PantallaDeSistemas } from './sistemas';
import { PantallaDeTenants } from './tenants';
import { PantallaDeUsuarios } from './usuarios';

/**
 * Las rutas de la aplicación (KAN-142).
 *
 * El árbol dice quién puede ver qué:
 *
 * - `/login` es lo único abierto, igual que en el backend, donde
 *   `POST /api/v1/auth/login` es la única ruta pública junto con el health.
 * - Todo lo demás cuelga de una {@link RutaProtegida}, que sin sesión manda al
 *   login.
 * - `/admin` además pide rol `ADMIN` ({@link RequiereRol}).
 *
 * **La protección de verdad está en el backend** (deny-by-default; `/admin/**`
 * exige `ROLE_ADMIN`). Esto es para que la app no ofrezca puertas cerradas y no
 * se rompa cuando alguien escribe una URL a mano.
 *
 * El `<BrowserRouter>` no está acá sino en `main.tsx`, para que los tests
 * puedan montar estas mismas rutas con un `<MemoryRouter>` y entrar por
 * cualquier URL.
 */
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <RutaProtegida>
            <Layout />
          </RutaProtegida>
        }
      >
        <Route path="/" element={<Inicio />} />
        {/* Sin RequiereRol: ADMIN y OPERADOR son los únicos roles que existen
            y los dos pueden emitir. Lo que sí limita quién factura es la
            lista blanca de tenants de cada SISTEMA conectado (KAN-144); una
            persona autenticada puede emitir para cualquier tenant. */}
        <Route path="/emision" element={<PantallaDeEmision />} />
        {/* Listado y detalle (KAN-146), también sin RequiereRol: quien emite
            tiene que poder mirar lo que emitió. El detalle va por el id INTERNO
            del comprobante y no por su número, que se repite entre puntos de
            venta y entre tipos. */}
        <Route path="/comprobantes" element={<PantallaDeComprobantes />} />
        <Route path="/comprobantes/:id" element={<DetalleDeComprobante />} />
        {/* El contrato de la API (KAN-184), también sin RequiereRol: para leer cómo
            funciona la API no hace falta ser administrador, alcanza con tener un
            usuario. Lo que no se ofrece es verlo sin sesión: el contrato describe la
            superficie completa y el backend lo sirve solo con credencial. */}
        <Route path="/contrato" element={<PantallaDelContrato />} />
        {/* Listado de tenants (solo lectura), también sin `RequiereRol`: usa
            `/api/v1/consultas/tenants`, la misma superficie que ya ve un
            OPERADOR en el selector de emisión y en la lista blanca de un
            sistema. Existe para encontrar el id de un tenant sin ir a la
            base — por ejemplo, para autorizarlo en un sistema conectado. */}
        <Route path="/tenants" element={<PantallaDeTenants />} />
        <Route
          path="/admin"
          element={
            <RequiereRol rol="ADMIN">
              <Administracion />
            </RequiereRol>
          }
        />
        {/* Cada pantalla de administración repite `RequiereRol` en vez de
            colgar de una ruta padre que lo aplique una sola vez. Es a
            propósito: así agregar una ruta bajo /admin no hereda la protección
            por accidente de dónde se la pegó, y quien lea el árbol ve en cada
            línea quién puede entrar. Es la misma idea que el deny-by-default
            del backend, donde abrir una ruta exige tocar `SeguridadConfig`. */}
        <Route
          path="/admin/usuarios"
          element={
            <RequiereRol rol="ADMIN">
              <PantallaDeUsuarios />
            </RequiereRol>
          }
        />
        <Route
          path="/admin/sistemas"
          element={
            <RequiereRol rol="ADMIN">
              <PantallaDeSistemas />
            </RequiereRol>
          }
        />
      </Route>
      <Route path="*" element={<NoExiste />} />
    </Routes>
  );
}

function NoExiste() {
  return (
    <main>
      <h1>No existe esa página</h1>
      <p>
        <Link to="/">Volver al inicio</Link>
      </p>
    </main>
  );
}
